import React from 'react';
import { ChangeEvent } from 'react';
import { Box, FormControlLabel, FormGroup, Stack, Typography, useTheme } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IconWhatsapp } from '@shared/assets/images/svgs';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { MessageTemplateField } from '@shared/components/messageTemplateField';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { ExpandableSettingsBlock, StaticSettingsBlock } from './SettingsBlocks';
import { TemplatePreviewValues, ToolPanelProps } from './types';

type Props = ToolPanelProps & { templatePreviewValues: TemplatePreviewValues };

export const WhatsAppSettingsPanel: React.FC<Props> = ({
  isReadOnly,
  expanded,
  onExpandedChange,
  templatePreviewValues,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control } = useFormContext<AgentToolsFormValue>();
  const sendFollowUpEnabled = useWatch({
    control,
    name: 'sendFollowUpMessageTool.enabled',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ExpandableSettingsBlock
        id="whatsapp"
        expanded={expanded.whatsapp}
        onChange={(nextExpanded) => onExpandedChange('whatsapp', nextExpanded)}
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                'svg path': {
                  fill: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconWhatsapp />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.whatsAppMessageCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={
                  <Controller
                    name="sendFollowUpMessageTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.whatsAppMessageCheckbox'),
                        }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                          onExpandedChange('whatsapp', e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }>
        <Box>
          <MessageTemplateField<AgentToolsFormValue>
            fieldName="sendFollowUpMessageTool.messageTemplate"
            previewValues={templatePreviewValues}
            disabled={isReadOnly}
            required={Boolean(sendFollowUpEnabled)}
          />
        </Box>
      </ExpandableSettingsBlock>

      <StaticSettingsBlock
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                'svg path': {
                  fill: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconWhatsapp />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.generateWhatsappMessageCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={
                  <Controller
                    name="generateWhatsappMessageTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.generateWhatsappMessageCheckbox'),
                        }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }
      />
    </Box>
  );
};
